import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { httpService } from "../../../service/httpService";

const getInitialToken = (): string | null => {
  return typeof window !== "undefined" ? localStorage.getItem("authToken") : null;
};

const getInitialRefreshToken = (): string | null => {
  return typeof window !== "undefined" ? localStorage.getItem("refreshToken") : null;
};

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  userRole: string | null;
  userFullName: string | null;
  isAuthenticated: boolean;
  hydrated: boolean;
  rmcId: string | null;
  userId: string | null; // JWT ID
  clientName: string | null;
}

const initialState: AuthState = {
  token: getInitialToken(),
  refreshToken: getInitialRefreshToken(),
  userRole: null,
  userFullName: null,
  isAuthenticated: !!getInitialToken(),
  hydrated: false,
  rmcId: null,
  userId: null,
  clientName: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    _applyRehydratedAuth(state, action: PayloadAction<Partial<AuthState>>) {
      Object.assign(state, action.payload);
      state.hydrated = true;
    },

    login(state, action: PayloadAction<{ token: string; refreshToken?: string }>) {
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken || null;
      state.isAuthenticated = true;
      state.hydrated = true;

      localStorage.setItem("authToken", action.payload.token);
      if (action.payload.refreshToken) {
        localStorage.setItem("refreshToken", action.payload.refreshToken);
      }

      try {
        const payload = JSON.parse(atob(action.payload.token.split(".")[1]));
        state.userId = payload.sub || null;
        state.clientName =
          payload.clientName ||
          payload["clientName"] ||
          payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/clientname"] ||
          null;
      } catch {
        state.userId = null;
        state.clientName = null;
      }

      // ✅ Apply Authorization header for immediate API use
      httpService.setAuthToken(action.payload.token);
    },

    setUserRole(state, action: PayloadAction<string>) {
      state.userRole = action.payload;
    },

    setUserFullName(
      state,
      action: PayloadAction<{ given_name?: string; family_name?: string; fallback?: string }>
    ) {
      if (action.payload.given_name && action.payload.family_name) {
        state.userFullName = `${action.payload.given_name} ${action.payload.family_name}`;
      } else {
        state.userFullName = action.payload.fallback || "";
      }
    },

    setUserRmcId(state, action) {
      state.rmcId = action.payload;
    },

    setJti(state, action: PayloadAction<string | null>) {
      state.userId = action.payload;
    },

    logout(state) {
      state.token = null;
      state.refreshToken = null;
      state.userRole = null;
      state.userFullName = null;
      state.isAuthenticated = false;
      state.hydrated = true;
      state.rmcId = null;
      state.userId = null;

      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");

      // ✅ Clear Authorization header
      httpService.setAuthToken(null);
    },
  },
});

// ✅ Thunk version of rehydrateAuth (replaces old reducer)
export const rehydrateAuth = () => (dispatch: any) => {
  const token = typeof window !== "undefined" ? localStorage.getItem("authToken") : null;
  const refreshToken = typeof window !== "undefined" ? localStorage.getItem("refreshToken") : null;

  const stateUpdate: Partial<AuthState> = {
    token,
    refreshToken,
    isAuthenticated: !!token,
  };

  if (token) {
    // ✅ Re-apply token to Axios headers
  httpService.setAuthToken(token);
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));

      stateUpdate.userRole =
        payload["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] ||
        payload.role ||
        null;

      stateUpdate.userFullName =
        payload.given_name && payload.family_name
          ? `${payload.given_name} ${payload.family_name}`
          : payload.name || "";

      stateUpdate.userId = payload.sub || null;

      stateUpdate.clientName =
        payload.clientName ||
        payload["clientName"] ||
        payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/clientname"] ||
        null;
    } catch (err) {
      console.warn("⚠️ Error decoding token payload during rehydrateAuth:", err);
    }
  }

  dispatch(authSlice.actions._applyRehydratedAuth(stateUpdate));
};

export const {
  login,
  setUserRole,
  setUserFullName,
  logout,
  setUserRmcId,
  setJti,
} = authSlice.actions;

export default authSlice.reducer;
